import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { randomUuid } from './useIdempotencyKey';
import {
  uploadPendingOrderFile,
  discardPendingOrderUploads,
  claimPendingOrderUploads,
  savedItemIdFor,
} from '../utils/orderFileMutations';

/**
 * PENDING ORDER UPLOADS — upload on pick, attach on save (R13, 2026-09-15).
 *
 * Owner, shown "Driver USB-INFRARED (1).ZIP (7.1 MB) Staged Remove": *"What does
 * staged mean? If this means uploaded please chnge it to uploaded"*, then:
 * *"upload the file straight away, delete it if the user does not save the line
 * or they delete the line"*. REPLACES the R10 client-side staging, where the
 * `File` waited in the browser until Save.
 *
 * ── THE MODEL ─────────────────────────────────────────────────────────────
 * A picked file uploads AT ONCE to the caller's pending store
 * (`POST /api/order-uploads/pending`) and is held here against a LINE KEY:
 *   `order`         — an order-level attachment (`PENDING_ORDER_LINE`);
 *   `id:<itemId>`   — a saved line (the full order page's edit mode);
 *   `new:<client_key>` — a line with no id yet (never its index).
 * The SAVE claims each line's uploaded files onto the saved order
 * (`claimPlanned`); ✕, removing a line, Discard, unmount and page hide DISCARD.
 * The server's 24h sweep catches whatever a closed tab leaves.
 *
 * Each entry: `{ key, file, lineKey, state: 'uploading'|'uploaded'|'failed',
 * fraction, error }`. `key` is the upload's client-minted UUID and its server name.
 *
 * 🔑 **THE ENTRY LIST LIVES IN A REF, NOT ONLY IN STATE.** A successful claim is
 * followed at once by the caller's `onSaved`, which unmounts the component — and
 * the unmount DISCARDS whatever is still listed. Removing claimed entries from a
 * `setState` would still read them in that cleanup and delete a file the order
 * now owns. The ref is updated synchronously; a counter re-renders.
 *
 * 🪤 **A claim attempt reuses its `Idempotency-Key` only for the SAME key set.**
 * The server replays a stored 4xx for a repeated key regardless of the body, so
 * a retry after the user removed one file must be a new attempt.
 *
 * ⚠️ Known and accepted: ✕ on a file whose bytes already reached the server but
 * whose response had not, aborts the request and discards the key — if the
 * server's insert lands AFTER the discard, that row waits for the 24h sweep.
 */
export const PENDING_ORDER_LINE = 'order';

/** The line key for a form line: its id if saved, else its `client_key`; null when it has neither. */
export function pendingLineKeyFor(item) {
  if (!item) return null;
  if (item.id != null) return `id:${item.id}`;
  return item.client_key ? `new:${item.client_key}` : null;
}

export const PENDING_UPLOADS_IN_FLIGHT_MESSAGE = 'Wait for the attachments to finish uploading before saving.';
export const PENDING_UPLOADS_FAILED_MESSAGE = 'An attachment did not upload — retry or remove it before saving.';
/** The server's code for a key that is not (or no longer) the caller's pending upload. */
export const PENDING_UPLOAD_NOT_FOUND = 'pending_upload_not_found';
/** The row's own error once a claim found its upload gone (review M5). */
export const PENDING_UPLOAD_EXPIRED_ENTRY = 'Expired before it was attached — retry to upload it again, or remove it.';
/** A save's report for those files. */
export const PENDING_UPLOADS_EXPIRED_MESSAGE = 'expired before the save — retry or remove the expired file(s)';

/** Why a line's files cannot go with this save: it is in the form but its blank description drops it from the payload. */
export function blockedLineMessage(names) {
  return `Add a description to the new line before saving — ${names.join(', ')} cannot be attached to a line that is not saved.`;
}

export default function usePendingOrderUploads() {
  const entriesRef = useRef([]);
  const controllersRef = useRef(new Map());
  const attemptsRef = useRef(new Map());
  const [, rerender] = useReducer((n) => n + 1, 0);
  const mountedRef = useRef(true);

  const commit = useCallback((next) => {
    entriesRef.current = next;
    if (mountedRef.current) rerender();
  }, []);
  const patch = useCallback((key, change) => {
    commit(entriesRef.current.map((e) => (e.key === key ? { ...e, ...change } : e)));
  }, [commit]);

  const startUpload = useCallback((entry) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) controllersRef.current.set(entry.key, controller);
    uploadPendingOrderFile(entry.key, entry.file, {
      signal: controller ? controller.signal : null,
      onProgress: (fraction) => {
        const current = entriesRef.current.find((e) => e.key === entry.key);
        if (current && current.state === 'uploading') patch(entry.key, { fraction: Math.max(current.fraction, fraction) });
      },
    }).then(() => {
      if (entriesRef.current.some((e) => e.key === entry.key)) patch(entry.key, { state: 'uploaded', fraction: 1, error: null });
    }).catch((err) => {
      if (err && err.name === 'AbortError') return;
      if (entriesRef.current.some((e) => e.key === entry.key)) {
        patch(entry.key, { state: 'failed', error: (err && err.message) || 'Upload failed' });
      }
    }).finally(() => {
      controllersRef.current.delete(entry.key);
    });
  }, [patch]);

  /** Upload these files now, against one line. */
  const add = useCallback((lineKey, files) => {
    const list = Array.from(files || []);
    if (!lineKey || list.length === 0) return;
    const added = list.map((file) => ({ key: randomUuid(), file, lineKey, state: 'uploading', fraction: 0, error: null }));
    commit([...entriesRef.current, ...added]);
    added.forEach(startUpload);
  }, [commit, startUpload]);

  const dropKeys = useCallback((keys, { keepalive = false } = {}) => {
    const set = new Set(keys);
    if (set.size === 0) return;
    set.forEach((key) => {
      const c = controllersRef.current.get(key);
      if (c) c.abort();
      controllersRef.current.delete(key);
    });
    commit(entriesRef.current.filter((e) => !set.has(e.key)));
    discardPendingOrderUploads([...set], { keepalive });
  }, [commit]);

  /** ✕ — abort if still uploading, drop it, and delete it on the server. */
  const remove = useCallback((key) => dropKeys([key]), [dropKeys]);

  /** A removed line takes its files with it. */
  const removeLine = useCallback((lineKey) => {
    dropKeys(entriesRef.current.filter((e) => e.lineKey === lineKey).map((e) => e.key));
  }, [dropKeys]);

  /** Discard / cancel / leave: everything not yet claimed goes. */
  const discardAll = useCallback((opts) => {
    dropKeys(entriesRef.current.map((e) => e.key), opts);
  }, [dropKeys]);

  /**
   * A FAILED upload: try again with the same File, under a new key — and
   * DISCARD the old key (review M3). "Failed" is what the CLIENT saw: a lost
   * response can leave the old row and bytes on the server, where they would
   * count against the per-user cap until the 24h sweep.
   */
  const retry = useCallback((key) => {
    const entry = entriesRef.current.find((e) => e.key === key && e.state === 'failed');
    if (!entry) return;
    const fresh = { ...entry, key: randomUuid(), state: 'uploading', fraction: 0, error: null };
    commit(entriesRef.current.map((e) => (e.key === key ? fresh : e)));
    discardPendingOrderUploads([key]);
    startUpload(fresh);
  }, [commit, startUpload]);

  /** A line gained an id (its save landed but a claim did not): its files follow it. */
  const rekeyLine = useCallback((from, to) => {
    if (!from || !to || from === to) return;
    commit(entriesRef.current.map((e) => (e.lineKey === from ? { ...e, lineKey: to } : e)));
  }, [commit]);

  const entriesFor = useCallback((lineKey) => entriesRef.current.filter((e) => e.lineKey === lineKey), []);

  /**
   * Can a save go ahead, and which lines will it claim? `formItems` are the
   * form's lines, `payloadItems` what the save will send (a blank-description
   * line is dropped by `buildPayload`, so its files would have nowhere to go).
   * Returns `{ message }` when the save must be refused, else `{ lines }`.
   */
  const planClaims = useCallback((formItems, payloadItems) => {
    const all = entriesRef.current;
    if (all.some((e) => e.state === 'uploading')) return { message: PENDING_UPLOADS_IN_FLIGHT_MESSAGE };
    if (all.some((e) => e.state === 'failed')) return { message: PENDING_UPLOADS_FAILED_MESSAGE };
    const formList = Array.isArray(formItems) ? formItems : [];
    const formIds = new Set(formList.map((i) => i && i.id).filter((v) => v != null).map(String));
    const formKeys = new Set(formList.map((i) => i && i.client_key).filter(Boolean));
    const sentKeys = new Set((Array.isArray(payloadItems) ? payloadItems : []).map((i) => i && i.client_key).filter(Boolean));
    const lines = [];
    const blocked = [];
    [...new Set(all.map((e) => e.lineKey))].forEach((lineKey) => {
      if (lineKey === PENDING_ORDER_LINE) {
        lines.push({ lineKey, itemId: null, clientKey: null, orderLevel: true });
      } else if (lineKey.startsWith('id:')) {
        const id = lineKey.slice(3);
        if (formIds.has(id)) lines.push({ lineKey, itemId: Number(id), clientKey: null, orderLevel: false });
      } else if (lineKey.startsWith('new:')) {
        const ck = lineKey.slice(4);
        if (!formKeys.has(ck)) return;
        if (sentKeys.has(ck)) lines.push({ lineKey, itemId: null, clientKey: ck, orderLevel: false });
        else blocked.push(...all.filter((e) => e.lineKey === lineKey).map((e) => e.file.name));
      }
    });
    if (blocked.length > 0) return { message: blockedLineMessage(blocked) };
    return { lines };
  }, []);

  /**
   * After the save landed: claim each planned line onto `orderId`, resolving a
   * new line's id from `savedItems` by its echoed `client_key` (never position).
   * Every line is attempted; returns `[{ lineKey, names, message, line }]` for
   * those that did not attach — `line` is what a retry passes back (a new line
   * that did get an id is RE-KEYED to it, so the retry claims it as a saved
   * line). Never throws.
   *
   * 🚨 **AT MOST ONE FAILURE PER LINE** (re-check IMP-1, 2026-09-15). One line
   * can fail several ways in one save (an expired file AND a refused one), and
   * `NewOrder` retries with `failures.map((f) => f.line)` — two failures for one
   * line put that line in the retry TWICE, so it was claimed twice per pass and
   * the failures doubled every resubmit (2 → 4 → 8). `report` merges by line:
   * every file name, every distinct message, one `line`. The dedupe is HERE
   * rather than in NewOrder so no caller can meet the duplicate.
   */
  const claimPlanned = useCallback(async (orderId, lines, savedItems) => {
    const failures = [];
    const messagesByLine = new Map();
    const report = (lineKey, names, message, line) => {
      const existing = failures.find((f) => f.lineKey === lineKey);
      if (!existing) {
        failures.push({ lineKey, names: [...names], message, line });
        messagesByLine.set(lineKey, [message]);
        return;
      }
      existing.names.push(...names);
      const messages = messagesByLine.get(lineKey);
      if (!messages.includes(message)) {
        messages.push(message);
        existing.message = messages.join('; ');
      }
    };

    /** One claim ATTEMPT for these keys, reusing its Idempotency-Key only for the same key set. */
    const claimKeys = async (attemptId, lineKey, itemId, keys) => {
      const signature = `${lineKey}|${itemId}|${keys.slice().sort().join(',')}`;
      let attempt = attemptsRef.current.get(attemptId);
      if (!attempt || attempt.signature !== signature) {
        attempt = { signature, key: randomUuid() };
        attemptsRef.current.set(attemptId, attempt);
      }
      await claimPendingOrderUploads(orderId, keys, { itemId, idempotencyKey: attempt.key });
      attemptsRef.current.delete(attemptId);
      const claimed = new Set(keys);
      commit(entriesRef.current.filter((e) => !claimed.has(e.key)));
    };

    for (const line of Array.isArray(lines) ? lines : []) {
      const allEntries = entriesRef.current.filter((e) => e.lineKey === line.lineKey);
      if (allEntries.length === 0) continue;
      const itemId = line.orderLevel
        ? null
        : (line.itemId != null ? line.itemId : savedItemIdFor({ client_key: line.clientKey }, savedItems));
      if (!line.orderLevel && itemId == null) {
        report(line.lineKey, allEntries.map((e) => e.file.name), 'The saved line could not be identified', line);
        continue;
      }
      let lineKey = line.lineKey;
      if (!line.orderLevel && lineKey.startsWith('new:')) {
        rekeyLine(lineKey, `id:${itemId}`);
        lineKey = `id:${itemId}`;
      }
      const retryLine = { lineKey, itemId, clientKey: null, orderLevel: line.orderLevel };

      // Review M5: a file that is not "uploaded" (still going, refused, or marked
      // EXPIRED by an earlier claim) cannot go with this claim — and must be
      // REPORTED, so a retry that skips `planClaims` (NewOrder's partial path)
      // never reads as a clean success while a file sits unattached.
      const notReady = allEntries.filter((e) => e.state !== 'uploaded');
      if (notReady.length > 0) {
        report(lineKey, notReady.map((e) => e.file.name), PENDING_UPLOADS_FAILED_MESSAGE, retryLine);
      }
      const ready = allEntries.filter((e) => e.state === 'uploaded');
      if (ready.length === 0) continue;
      const keys = ready.map((e) => e.key);

      try {
        // eslint-disable-next-line no-await-in-loop
        await claimKeys(lineKey, lineKey, itemId, keys);
      } catch (err) {
        if (!(err && err.code === PENDING_UPLOAD_NOT_FOUND)) {
          report(lineKey, ready.map((e) => e.file.name), (err && err.message) || 'Failed to attach the uploaded files', retryLine);
          continue;
        }
        // 🔑 Review M5: the server answers ONE 404 for the whole set (no oracle
        // for which key), so one expired file used to fail the line on every
        // save, for good. Claim each file alone: the live ones attach, the
        // expired ones are marked failed — Retry re-uploads the File still held
        // here, ✕ removes it.
        attemptsRef.current.delete(lineKey);
        const expired = [];
        const refused = [];
        for (const entry of ready) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await claimKeys(`${lineKey}#${entry.key}`, lineKey, itemId, [entry.key]);
          } catch (oneErr) {
            if (oneErr && oneErr.code === PENDING_UPLOAD_NOT_FOUND) {
              expired.push(entry);
              patch(entry.key, { state: 'failed', fraction: 0, error: PENDING_UPLOAD_EXPIRED_ENTRY });
            } else {
              refused.push({ entry, message: (oneErr && oneErr.message) || 'Failed to attach the uploaded files' });
            }
          }
        }
        if (expired.length > 0) {
          report(lineKey, expired.map((e) => e.file.name), PENDING_UPLOADS_EXPIRED_MESSAGE, retryLine);
        }
        refused.forEach(({ entry, message }) => {
          report(lineKey, [entry.file.name], message, retryLine);
        });
      }
    }
    return failures;
  }, [commit, patch, rekeyLine]);

  // Leaving the page: best-effort discard that survives the unload.
  // 🪤 Review M4: NOT when `event.persisted` — the page is going into the
  // back/forward cache with this state intact, and Back restores it listing
  // files the server would already have deleted (the save then 404s). A cached
  // page that is never restored leaves them to the 24h sweep.
  useEffect(() => {
    const onPageHide = (event) => {
      if (event && event.persisted) return;
      const keys = entriesRef.current.map((e) => e.key);
      if (keys.length > 0) discardPendingOrderUploads(keys, { keepalive: true });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // Unmount (collapse, navigate away, department reset): nothing unclaimed survives.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const keys = entriesRef.current.map((e) => e.key);
      controllersRef.current.forEach((c) => c.abort());
      controllersRef.current.clear();
      entriesRef.current = [];
      if (keys.length > 0) discardPendingOrderUploads(keys, { keepalive: true });
    };
  }, []);

  const entries = entriesRef.current;
  return useMemo(() => ({
    entries,
    entriesFor,
    add,
    remove,
    retry,
    removeLine,
    discardAll,
    rekeyLine,
    planClaims,
    claimPlanned,
    hasEntries: entries.length > 0,
    uploading: entries.some((e) => e.state === 'uploading'),
    failed: entries.some((e) => e.state === 'failed'),
  }), [entries, entriesFor, add, remove, retry, removeLine, discardAll, rekeyLine, planClaims, claimPlanned]);
}
